import React from "react";
import { motion } from "framer-motion";
import { LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSoulProfile } from "@/lib/useSoulProfile";
import { NAV } from "@/constants/testIds";

/**
 * HeaderLogout — a floating, top-right log-out control that lives outside
 * the side/mobile nav so authenticated seekers can always close the veil
 * from any page. Sits just left of the Narrator cluster.
 */
export default function HeaderLogout() {
  const { user, logout } = useAuth();
  const { profile } = useSoulProfile();
  const navigate = useNavigate();
  if (!user) return null;

  const soulName = profile?.profile?.soul_name;
  const displayName =
    soulName || user.name || user.email?.split("@")[0] || "Seeker";

  const onClick = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
      className="fixed top-4 right-[10.5rem] z-[60] flex items-center gap-2 sm:right-[10.5rem]"
    >
      {/* Compact seeker name (hidden on mobile to save space) */}
      <div
        className="hidden sm:block font-overline text-[10px] tracking-[0.24em] px-3 py-1 rounded-full"
        style={{
          color: "#C8A96E",
          background: "rgba(15,15,15,0.55)",
          border: "1px solid rgba(212,175,55,0.25)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          maxWidth: "18ch",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={displayName}
      >
        {displayName}
      </div>

      {/* Logout icon button — always visible when authenticated */}
      <button
        type="button"
        data-testid={NAV.logoutBtnHeader}
        onClick={onClick}
        aria-label="Log out"
        title="Log out"
        className="flex items-center justify-center h-10 w-10 rounded-full transition-all"
        style={{
          background: "rgba(15,15,15,0.55)",
          border: "1px solid rgba(212,175,55,0.35)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          color: "#D4AF37",
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
        }}
      >
        <LogOut size={16} strokeWidth={1.5} />
      </button>
    </motion.div>
  );
}
